import { appointmentSlot, internalMeetingDays } from '@ghp/domain'
import type { CSSProperties } from 'react'

import type { CachedInquiry, CachedInternalMeeting } from '../../../offline/data-db'
import { isHandedOver, type TourState } from '../../inspections/lib/list-filters'

/**
 * La pose des cartes sur la gouttière horaire : à quelle hauteur, et sur quelle colonne quand
 * deux créneaux se chevauchent — rendez-vous et points internes **ensemble**, parce qu'ils
 * occupent la même équipe sur la même journée.
 *
 * ⚠️ **La maquette empile les cartes qui se chevauchent** — deux rendez-vous à 9 h y sont l'un
 * sur l'autre, le second illisible. Le défaut n'est pas reproduit : un contrôle qu'on ne lit pas
 * est un contrôle qu'on oublie de faire. Les cartes d'un même créneau se partagent la largeur du
 * jour.
 */

/** Ce qu'un rendez-vous occupe visuellement, faute de durée au modèle (`Appointment`, M24). */
const SLOT_MINUTES = 60

/**
 * Ce que l'agenda pose sur la grille : un rendez-vous de la tournée, ou un **point interne** de
 * l'équipe (#319). Discriminé par `kind` plutôt que par la forme : les deux portent `id` et
 * `scheduledAt`, et seul le premier s'ouvre.
 */
export type AgendaItem =
  | { kind: 'appointment'; inquiry: CachedInquiry }
  | { kind: 'internal'; meeting: CachedInternalMeeting }

/** Les deux natures de créneau d'une période, mises sur la même liste. */
export function agendaItemsOf(
  inquiries: readonly CachedInquiry[],
  meetings: readonly CachedInternalMeeting[] = [],
): AgendaItem[] {
  return [
    ...inquiries.map((inquiry) => ({ kind: 'appointment' as const, inquiry })),
    ...meetings.map((meeting) => ({ kind: 'internal' as const, meeting })),
  ]
}

/**
 * Les points internes de la période, **un par jour** qu'ils occupent (#576) : un séminaire de trois
 * jours se pose sur chacun, son jour d'affichage dans `scheduledDay`. Bornes comprises.
 */
export function meetingsOnDays(
  meetings: readonly CachedInternalMeeting[],
  fromDay: string,
  toDay: string,
): CachedInternalMeeting[] {
  return meetings.flatMap((meeting) =>
    internalMeetingDays(meeting.scheduledAt, meeting.durationMinutes)
      .filter((day) => day >= fromDay && day <= toDay)
      .map((day) => ({ ...meeting, scheduledDay: day })),
  )
}

/** L'identifiant du créneau, quelle que soit sa nature — la clé d'une carte. */
export function itemKey(item: AgendaItem): string {
  return item.kind === 'appointment' ? item.inquiry.id : item.meeting.id
}

/** Le jour du créneau à Paris, ou `null` quand sa date est illisible. */
export function itemDay(item: AgendaItem): string | null {
  return (
    (item.kind === 'appointment' ? item.inquiry.scheduledDay : item.meeting.scheduledDay) ?? null
  )
}

/** L'instant du créneau tel qu'il est servi — chaîne vide quand il manque. */
export function scheduledAtOf(item: AgendaItem): string {
  return (item.kind === 'appointment' ? item.inquiry.scheduledAt : item.meeting.scheduledAt) ?? ''
}

/** Une journée de travail : ce qu'un rendez-vous sur plusieurs jours occupe sur le premier (#431). */
const WORKDAY_MINUTES = 8 * 60

/**
 * Ce que le créneau occupe : la **durée saisie** d'un point interne (M28) ou d'un rendez-vous
 * (#431), une heure pour un rendez-vous en cache d'avant elle.
 *
 * Sur plusieurs jours, la carte tient dans la journée de travail : un séminaire (#576) la reprend
 * sur chacun de ses jours (`meetingsOnDays`). ponytail: un rendez-vous sur plusieurs jours ne se
 * montre que sur son premier jour — le cas est rare (choix du 15/10).
 */
function minutesLong(item: AgendaItem): number {
  const minutes =
    item.kind === 'internal'
      ? item.meeting.durationMinutes
      : (item.inquiry.appointmentDurationMinutes ?? SLOT_MINUTES)

  return Math.min(minutes, WORKDAY_MINUTES)
}

/**
 * Le créneau est-il **fait** (#577) ? Un rendez-vous ou un point interne dont l'heure de fin est
 * passée, ou un contrôle transmis ou en file, même à venir. L'agenda le grise — estompé, jamais
 * caché : c'est l'avancée de la journée que GHP veut y lire.
 *
 * ⚠️ Un contrôle **renvoyé** par le bureau n'est jamais fait, même à une date passée : il est à
 * refaire. Une date illisible non plus — rien ne dit que le créneau est derrière l'équipe.
 */
export function isDone(
  item: AgendaItem,
  now: Date,
  state: TourState | undefined,
  queued: boolean,
): boolean {
  if (item.kind === 'appointment') {
    if (state === 'returned') return false
    if (queued || (state !== undefined && isHandedOver(state))) return true
  }

  // La vraie fin, et non la hauteur de la carte : `minutesLong` borne un rendez-vous de plusieurs
  // jours à la journée de travail, et le griserait dès le premier soir.
  const minutes =
    item.kind === 'internal'
      ? item.meeting.durationMinutes
      : (item.inquiry.appointmentDurationMinutes ?? SLOT_MINUTES)
  const end = new Date(scheduledAtOf(item)).getTime() + minutes * 60_000

  return end <= now.getTime()
}

const MINUTES_PER_HOUR = 60

/** La gouttière par défaut, quand la journée n'a rien à dire — une journée de travail. */
export const DEFAULT_GUTTER: Gutter = { fromHour: 8, toHour: 18 }

/** Les heures que la gouttière affiche, **bornes comprises pour `fromHour`**. */
export interface Gutter {
  fromHour: number
  /** Exclusive : une gouttière `8 → 18` se termine à 18 h 00 pile. */
  toHour: number
}

/** Où une carte se pose : sa position et sa colonne dans son groupe de chevauchement. */
export interface SlotPlacement {
  /** L'heure du créneau à Paris (`08:30`) — une date illisible n'arrive jamais jusqu'ici. */
  time: string
  /** Le haut de la carte, en pourcentage de la hauteur de la gouttière. */
  top: number
  /**
   * La hauteur de la carte, en pourcentage — un créneau forfaitaire pour un rendez-vous, sa durée
   * pour un point interne.
   */
  height: number
  /** La colonne de la carte (0 en premier) et le nombre de colonnes de son groupe. */
  lane: number
  lanes: number
}

/** Une carte posée : le créneau, et où il se pose. */
export interface PlacedSlot extends SlotPlacement {
  item: AgendaItem
}

/** Les minutes écoulées depuis minuit, **à l'heure de Paris** — celle que la carte affiche. */
function minutesOf(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)

  return hours * MINUTES_PER_HOUR + minutes
}

/** Les créneaux lisibles d'un ensemble, triés par heure — le reste est laissé de côté. */
function readable(items: readonly AgendaItem[]): { item: AgendaItem; time: string }[] {
  return items
    .flatMap((item) => {
      const scheduledAt = scheduledAtOf(item)
      const time = appointmentSlot(scheduledAt)?.time

      return time === undefined ? [] : [{ item, time, instant: Date.parse(scheduledAt) }]
    })
    .sort((left, right) => left.instant - right.instant)
}

/**
 * La gouttière **dérivée des rendez-vous**, jamais figée : un contrôle à 6 h 30 ou à 20 h existe
 * (rendez-vous pris pour un locataire qui part travailler), et une gouttière fixe le placerait
 * hors de l'écran — donc nulle part. Elle s'élargit plutôt que de couper.
 */
export function gutterOf(items: readonly AgendaItem[]): Gutter {
  const spans = readable(items).map(({ item, time }) => ({
    start: minutesOf(time),
    end: minutesOf(time) + minutesLong(item),
  }))

  if (spans.length === 0) return DEFAULT_GUTTER

  const earliest = Math.floor(Math.min(...spans.map(({ start }) => start)) / MINUTES_PER_HOUR)
  const latest = Math.ceil(Math.max(...spans.map(({ end }) => end)) / MINUTES_PER_HOUR)

  return {
    fromHour: Math.min(DEFAULT_GUTTER.fromHour, earliest),
    toHour: Math.max(DEFAULT_GUTTER.toHour, latest),
  }
}

/**
 * La hauteur minimale d'une carte, en px — **le même nombre** que le `min-block-size` de son module
 * (`AppointmentCard.module.css`, `InternalMeetingCard.module.css`) : l'un change, l'autre suit.
 */
export const CARD_MIN_PX: Record<AgendaItem['kind'], number> = { appointment: 104, internal: 66 }

/**
 * La hauteur d'une heure, en px, qui laisse **chaque carte tenir dans son créneau** : le créneau le
 * plus court, rapporté à la hauteur minimale de sa carte, fixe l'heure de toute la grille. `0`
 * quand rien n'est posé — la gouttière garde alors sa hauteur de toujours, qui reste un plancher
 * (`--agenda-hour`, `WeekGrid.module.css`).
 *
 * Sans elle, une heure de 72 px portait une carte de 104 : un rendez-vous de 9 h recouvrait celui
 * de 10 h, sans que leurs horaires se chevauchent. Les colonnes, elles, restent réservées aux
 * horaires qui se chevauchent vraiment (`placeDay`).
 *
 * ponytail: une seule hauteur pour la grille, pas une par heure — une demi-heure (la plus courte
 * des durées proposées) double donc la hauteur de toute la semaine, qui défile. Une heure
 * élastique par plage remplirait mieux l'écran, au prix d'une gouttière non linéaire.
 */
export function fittingHourPx(items: readonly AgendaItem[]): number {
  return Math.max(
    0,
    ...readable(items).map(({ item }) =>
      Math.ceil((CARD_MIN_PX[item.kind] * MINUTES_PER_HOUR) / minutesLong(item)),
    ),
  )
}

/** Les heures de la colonne de gauche, une par ligne. */
export function hoursOf({ fromHour, toHour }: Gutter): number[] {
  return Array.from({ length: Math.max(toHour - fromHour, 1) }, (_, index) => fromHour + index)
}

/**
 * Les cartes d'une journée, posées sur la gouttière.
 *
 * Les colonnes se calculent par **groupe de chevauchement** et non pour la journée entière :
 * sinon trois rendez-vous à 9 h rétréciraient aussi celui de 16 h, seul sur son créneau.
 */
export function placeDay(items: readonly AgendaItem[], gutter: Gutter): PlacedSlot[] {
  const span = Math.max(gutter.toHour - gutter.fromHour, 1) * MINUTES_PER_HOUR
  const placed: PlacedSlot[] = []
  /** La fin de la dernière carte de chaque colonne, en minutes depuis minuit. */
  const laneEnds: number[] = []
  /** Les cartes du groupe de chevauchement en cours, à corriger quand il se ferme. */
  let group: PlacedSlot[] = []

  const closeGroup = (): void => {
    for (const card of group) card.lanes = laneEnds.length
    group = []
    laneEnds.length = 0
  }

  for (const { item, time } of readable(items)) {
    const start = minutesOf(time)
    const length = minutesLong(item)

    // Le groupe se ferme quand plus aucune colonne n'est occupée à cette heure : ce qui suit ne
    // chevauche rien de ce qui précède, et hérite donc d'une largeur pleine.
    if (laneEnds.length > 0 && laneEnds.every((end) => end <= start)) closeGroup()

    const free = laneEnds.findIndex((end) => end <= start)
    const lane = free === -1 ? laneEnds.length : free
    laneEnds[lane] = start + length

    const card: PlacedSlot = {
      item,
      time,
      // Borné à la gouttière : elle est dérivée des rendez-vous, donc ce bornage ne coupe rien
      // en pratique — il évite qu'une gouttière forcée par un appelant pousse une carte hors de
      // l'écran, où elle serait perdue.
      top: clamp(((start - gutter.fromHour * MINUTES_PER_HOUR) / span) * 100),
      height: (length / span) * 100,
      lane,
      lanes: 1,
    }

    group.push(card)
    placed.push(card)
  }

  closeGroup()

  return placed
}

/**
 * La géométrie d'une carte en propriétés logiques — **en style en ligne**, et elle ne peut pas
 * être ailleurs : elle se calcule par carte, depuis l'heure du créneau et le nombre de colonnes
 * de son groupe. Écrite une fois pour les deux natures de carte, qui doivent tomber sur la même
 * gouttière au pixel près.
 */
export function placementStyle({ top, height, lane, lanes }: SlotPlacement): CSSProperties {
  const width = 100 / lanes

  return {
    insetBlockStart: `${String(top)}%`,
    blockSize: `${String(height)}%`,
    insetInlineStart: `${String(lane * width)}%`,
    inlineSize: `${String(width)}%`,
  }
}

function clamp(value: number): number {
  return Math.min(Math.max(value, 0), 100)
}
